import AvatarLightbox from "@/components/AvatarLightbox"
import ErrorDialog from "@/components/ErrorDialog"
import UsernameMark from "@/components/UsernameMark"
import { useAuth } from "@/hooks/useAuth"
import { useAuthModal } from "@/hooks/useAuthModal"
import { timeAgo } from "@/lib/filters"
import { conversationIdFor, publicProfilesFor } from "@/lib/profile"
import { privacyInbox, privacyPublicProfiles, privacySendMessage, privacyThread } from "@/lib/privacy"
import { listBlockedIds } from "@/lib/social"
import type { DirectMessage, Profile } from "@/types"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"

function belongsToUser(message: DirectMessage, userId: string) {
  return message.senderId === userId || message.recipientId === userId
}

function otherPartyId(message: DirectMessage, userId: string) {
  return message.senderId === userId ? message.recipientId : message.senderId
}

function mergeMessages(current: DirectMessage[], incoming: DirectMessage[]) {
  const unique = new Map<string, DirectMessage>()
  current.forEach((item) => unique.set(item._id, item))
  incoming.forEach((item) => unique.set(item._id, item))
  return Array.from(unique.values()).sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")))
}

export default function MessagesPage() {
  const { userId: routeUserId } = useParams()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const [messages, setMessages] = useState<DirectMessage[]>([])
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [draft, setDraft] = useState("")
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState("")
  const [photoOpen, setPhotoOpen] = useState(false)
  const [blockedIds, setBlockedIds] = useState<string[]>([])
  const threadEnd = useRef<HTMLDivElement>(null)
  const myId = user?.userId || ""
  const threadUserId = routeUserId && routeUserId !== myId ? routeUserId : ""

  const load = async (userId: string, options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true)
    try {
      const conversationId = threadUserId ? conversationIdFor(userId, threadUserId) : ""
      const [inbox, thread, blocked] = await Promise.all([
        conversationId ? Promise.resolve([] as DirectMessage[]) : privacyInbox(),
        conversationId && threadUserId ? privacyThread(threadUserId) : Promise.resolve([] as DirectMessage[]),
        listBlockedIds(userId),
      ])
      const blockedSet = new Set(blocked)
      const mine = [...inbox, ...thread]
        .filter((item): item is DirectMessage => {
          const message = item as DirectMessage
          if (!message?._id || !belongsToUser(message, userId)) return false
          if (blockedSet.has(otherPartyId(message, userId))) return false
          if (conversationId) {
            if (message.conversationId !== conversationId) return false
            if (otherPartyId(message, userId) !== threadUserId) return false
          }
          return true
        })
      const unique = new Map<string, DirectMessage>()
      mine.forEach((item) => unique.set(item._id, item))
      const nextMessages = Array.from(unique.values())
      const otherIds = Array.from(
        new Set(
          [threadUserId, ...nextMessages.map((message) => otherPartyId(message, userId))].filter((id) => id && id !== userId),
        ),
      )
      const people = otherIds.length ? await privacyPublicProfiles({ userIds: otherIds }) : []
      setBlockedIds(blocked)
      setMessages((prev) => (options?.silent ? mergeMessages(prev, nextMessages) : nextMessages))
      setProfiles(publicProfilesFor(people, otherIds))
    } catch (err) {
      console.error("Messages load failed:", err)
      if (!options?.silent) setError("Could not load messages.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setMessages([])
    setProfiles([])
    setDraft("")
    setPhotoOpen(false)
    if (!isAuthenticated || !myId) {
      openAuth()
      return
    }
    load(myId)
  }, [isAuthenticated, myId, threadUserId, openAuth])

  useEffect(() => {
    if (!isAuthenticated || !myId) return
    const timer = window.setInterval(() => load(myId, { silent: true }), 2500)
    return () => window.clearInterval(timer)
  }, [isAuthenticated, myId, threadUserId])

  const profileMap = useMemo(() => new Map(profiles.map((profile) => [profile.userId, profile])), [profiles])

  const threads = useMemo(() => {
    if (!myId) return []
    const latest = new Map<string, DirectMessage>()
    messages.forEach((message) => {
      const other = otherPartyId(message, myId)
      if (!other || other === myId) return
      const existing = latest.get(other)
      if (!existing || String(message.createdAt) > String(existing.createdAt)) latest.set(other, message)
    })
    return Array.from(latest.entries())
  }, [messages, myId])

  const threadMessages = useMemo(() => {
    if (!myId || !threadUserId) return []
    const conversationId = conversationIdFor(myId, threadUserId)
    return messages
      .filter((message) => belongsToUser(message, myId) && message.conversationId === conversationId)
      .sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")))
  }, [messages, myId, threadUserId])

  useEffect(() => {
    threadEnd.current?.scrollIntoView({ block: "end" })
  }, [threadMessages.length, threadUserId])

  const sendMessage = async () => {
    if (!isAuthenticated || !myId) {
      openAuth()
      return
    }
    if (!threadUserId || threadUserId === myId) return
    const blocked = await listBlockedIds(myId)
    if (blocked.includes(threadUserId)) {
      setError("Unblock this account before sending a message.")
      return
    }
    const text = draft.trim()
    if (!text) return
    const now = new Date().toISOString()
    const optimistic: DirectMessage = {
      _id: `local-${now}`,
      conversationId: conversationIdFor(myId, threadUserId),
      senderId: myId,
      recipientId: threadUserId,
      text,
      creator: myId,
      createdAt: now,
      updatedAt: now,
    }
    setDraft("")
    setMessages((prev) => mergeMessages(prev, [optimistic]))
    setSending(true)
    try {
      const created = await privacySendMessage(threadUserId, text)
      setMessages((prev) => {
        const withoutLocal = prev.filter((item) => item._id !== optimistic._id)
        return mergeMessages(withoutLocal, [created?._id ? created : optimistic])
      })
    } catch (err) {
      console.error("Send message failed:", err)
      setMessages((prev) => prev.filter((item) => item._id !== optimistic._id))
      setDraft(text)
      setError("Could not send that message.")
    } finally {
      setSending(false)
    }
  }

  if (!isAuthenticated) {
    return <p className="px-4 py-10 text-center text-sm text-[#6E6E6E]">Sign in to open your private messages.</p>
  }

  const threadProfile = threadUserId ? profileMap.get(threadUserId) : null

  return (
    <div className="flex h-[calc(100dvh-7.5rem)] min-h-0 flex-col overflow-hidden px-4 py-3 sm:px-6">
      {threadUserId ? (
        <>
          <div className="flex shrink-0 items-center gap-3">
            <button type="button" onClick={() => navigate("/messages")} className="text-xs text-[#A8A8A8]">
              Inbox
            </button>
            <button
              type="button"
              onClick={() => threadProfile?.avatarUrl && setPhotoOpen(true)}
              className="h-10 w-10 overflow-hidden rounded-full border border-[#1F1F1F] bg-[#141414]"
              aria-label="View profile photo"
            >
              {threadProfile?.avatarUrl ? <img src={threadProfile.avatarUrl} alt="" className="h-full w-full object-cover" /> : null}
            </button>
            <Link to={`/u/${threadUserId}`} className="min-w-0 flex-1">
              <h1 className="truncate font-[Syne] text-xl font-bold">{threadProfile?.displayName || "Collector"}</h1>
              <p className="text-xs text-[#6E6E6E]">
                <UsernameMark username={threadProfile?.username} userId={threadUserId} />
              </p>
            </Link>
          </div>
          <div className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto">
            {loading ? (
              <p className="text-center text-sm text-[#6E6E6E]">Loading thread...</p>
            ) : threadMessages.length === 0 ? (
              <p className="text-center text-sm text-[#6E6E6E]">No messages yet. Say hello.</p>
            ) : (
              threadMessages.map((message) => {
                const mine = message.senderId === myId
                const avatar = mine ? null : threadProfile?.avatarUrl
                return (
                  <div key={message._id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`}>
                    {!mine ? (
                      <button
                        type="button"
                        onClick={() => avatar && setPhotoOpen(true)}
                        className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-[#1F1F1F] bg-[#141414]"
                      >
                        {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> : null}
                      </button>
                    ) : null}
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#00C805] text-black" : "bg-[#141414] text-white"}`}>
                      <p>{message.text}</p>
                      <p className={`mt-1 text-[10px] ${mine ? "text-black/60" : "text-[#6E6E6E]"}`}>{timeAgo(message.createdAt)}</p>
                    </div>
                  </div>
                )
              })
            )}
            <div ref={threadEnd} />
          </div>
          {blockedIds.includes(threadUserId) ? (
            <p className="mt-3 shrink-0 pb-[env(safe-area-inset-bottom)] text-center text-sm text-[#6E6E6E]">Unblock this account to send a message.</p>
          ) : (
            <div className="mt-3 flex shrink-0 gap-2 pb-[env(safe-area-inset-bottom)]">
              <input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault()
                    sendMessage()
                  }
                }}
                placeholder="Message..."
                className="flex-1 rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none"
              />
              <button
                type="button"
                disabled={sending || !draft.trim()}
                onClick={sendMessage}
                className="rounded-xl bg-[#00C805] px-4 text-sm font-semibold text-black disabled:opacity-60"
              >
                {sending ? "..." : "Send"}
              </button>
            </div>
          )}
          <AvatarLightbox
            src={threadProfile?.avatarUrl}
            open={photoOpen}
            onClose={() => setPhotoOpen(false)}
            actionLabel="View profile"
            onAction={() => navigate(`/u/${threadUserId}`)}
          />
        </>
      ) : (
        <>
          <h1 className="shrink-0 font-[Syne] text-2xl font-bold">Messages</h1>
          <p className="mt-1 shrink-0 text-sm text-[#A8A8A8]">Private threads stay on your account. Other collectors cannot open them.</p>
          <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-center text-sm text-[#6E6E6E]">Loading inbox...</p>
            ) : threads.length === 0 ? (
              <p className="text-center text-sm text-[#6E6E6E]">No conversations yet. Message someone from their profile.</p>
            ) : (
              <div className="divide-y divide-[#1F1F1F] rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B]">
                {threads.map(([otherId, message]) => {
                  const profile = profileMap.get(otherId)
                  return (
                    <Link key={otherId} to={`/messages/${otherId}`} className="flex items-center gap-3 px-4 py-3">
                      <div className="h-11 w-11 overflow-hidden rounded-full border border-[#1F1F1F] bg-[#141414]">
                        {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" className="h-full w-full object-cover" /> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1 truncate text-sm font-semibold">
                          {profile?.displayName || "Collector"}
                          <UsernameMark username={profile?.username} userId={otherId} prefix={false} className="text-xs font-normal text-[#A8A8A8]" />
                        </p>
                        <p className="truncate text-xs text-[#6E6E6E]">{message.text}</p>
                      </div>
                      <span className="text-[10px] text-[#6E6E6E]">{timeAgo(message.createdAt)}</span>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}
      {error ? <ErrorDialog message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}
